import { useQuery } from '@tanstack/react-query';
import { sitecoreAxios } from '@pm2/api';
import { pm2ConfigStore } from '@pm2/store';
import { createSearchFriendlyString } from '@utils/common';
import { DAYS_TO_MS, HOURS_TO_MS } from '@utils/constants';
import { useLibrarySelector } from '../state/store.js';

function useGetLibraryQuery(extraConfig) {
  const portalId = pm2ConfigStore.useSelector(state => state.config.id);
  const previewMode = pm2ConfigStore.useSelector(state => state.previewMode);
  const libraryId = useLibrarySelector(state => state.libraryId);
  return useQuery({
    ...getLibraryQuery(portalId, extraConfig?.explicitLibraryId ?? libraryId, previewMode),
    ...extraConfig
  });
}
const getLibraryQuery = (portalId, libraryId, previewMode) => {
  return {
    queryKey: ["library", portalId, libraryId, previewMode],
    queryFn: () => getLibrary(portalId, libraryId, {
      previewMode
    }),
    staleTime: 8 * HOURS_TO_MS,
    gcTime: 7 * DAYS_TO_MS
  };
};
async function getLibrary(portalId, libraryId, options) {
  const url = `/api/v1/portals/${portalId}/libraries/${libraryId}`;
  const response = await sitecoreAxios.get(options?.previewMode ? `${process.env["PM2_PREVIEW_API"]}${url}` : `${process.env["PM2_API"]}${url}`);

  // Start by mapping folders and items to a usable data structure
  const groups = response.data.Folders.map(parseGroup);

  // Map data for filters
  const filters = [...response.data.Checkboxes.map(filter => parseFilter("checkboxes", filter)), ...response.data.Radiobuttons.map(filter => parseFilter("radiobuttons", filter)), ...response.data.Dropdowns.map(filter =>
  // NB: dropdowns have been remapped to use accordions in 2024, see ADO
  // #116834 for context
  parseFilter("accordions", filter))];
  return {
    id: response.data.Id,
    defaultView: response.data.GridView ? "grid" : "list",
    filters,
    groups
  };
}

/** Helper that parses groups and items into a well formatted data structure */
function parseGroup(group) {
  return {
    icon: group.IconLetter,
    id: group.Id,
    items: group.Items.map(item => {
      const thumb = (() => {
        if (!item.Thumb) {
          return undefined;
        }
        if (item.Template === "Videoplayer") {
          return `${process.env["NEXT_PUBLIC__VIDEO23_API"]}${item.Thumb}`;
        } else {
          return `${process.env["PM2_MEDIA_DOMAIN"]}/${item.Thumb}`;
        }
      })();
      const output = {
        // Parse out breadcrumbs
        breadcrumbs: (item.Breadcrumbs || []).map(breadcrumb => {
          const breadcrumbOutput = [];
          if (breadcrumb.FirstLevelTitle && breadcrumb.FirstLevelUrl) {
            breadcrumbOutput.push({
              path: breadcrumb.FirstLevelUrl,
              title: breadcrumb.FirstLevelTitle
            });
          }
          if (breadcrumb.SecondLevelTitle && breadcrumb.SecondLevelUrl) {
            breadcrumbOutput.push({
              path: breadcrumb.SecondLevelUrl,
              title: breadcrumb.SecondLevelTitle
            });
          }
          return breadcrumbOutput;
        }),
        id: item.Id,
        contentElementId: item.ContentElementId,
        thumb,
        __searchFriendlyContent: createSearchFriendlyString(`${item.Title} ${item.Description} ${item.Artist} ${item.Country} ${item.CreditLine} ${item.ImageText} ${item.Length} ${item.PageCount} ${item.Year}`),
        // will be overridden shortly
        type: "text"
      };
      switch (item.Template) {
        case "ComplexContent":
          output.type = "richtext";
          break;
        case "SimpleText":
        case "RichText":
          output.type = "text";
          break;
        case "ImageElement":
          output.type = "image";
          break;
        case "Audioplayer":
          output.type = "audio";
          break;
        case "Videoplayer":
          output.type = "video";
          break;
        case "Slider":
          output.type = "slider";
          break;
        case "Component":
        case "GiBook":
        case "GiBook4":
        case "TaskEngineLink":
          output.type = "iframe";
          break;
        case "Document":
          output.url = item.Url ? `${process.env["PM2_MEDIA_DOMAIN"]}/${item.Url}` : undefined;
          output.type = "document_link";
          break;
        case "ExternalLink":
          output.url = item.Url ? item.Url.indexOf("//") === -1 ? `${process.env["PM2_API"]}/${item.Url}` : item.Url : undefined;
          output.type = "external_link";
          break;
        case "InternalLink":
          output.url = item.Url ? item.Url.indexOf("//") === -1 ? `${process.env["PM2_API"]}/${item.Url}` : item.Url : undefined;
          output.type = "internal_link";
          break;
        // Unknown type?
      }
      if (item.Artist && item.ArtistVisibility) {
        output.artist = item.Artist;
      }
      if (item.Country && item.CountryVisibility) {
        output.country = item.Country;
      }
      if (item.CreditLine && item.CreditLineVisibility) {
        output.creditLine = item.CreditLine;
      }
      if (item.Description && item.DescriptionVisibility) {
        output.description = item.Description;
      }
      if (item.Length && item.LengthVisibility) {
        output.length = item.Length;
      }
      if (item.PageCount) {
        output.pageCount = item.PageCount;
      }
      if (item.Title && item.TitleVisibility) {
        output.title = item.Title;
      }
      if (item.Year && item.YearVisibility) {
        output.year = item.Year;
      }
      return output;
    }),
    title: group.Title
  };
}

/** Helper that parses a filter into a well formatted data structure */
function parseFilter(type, filter) {
  switch (type) {
    case "checkboxes":
    case "radiobuttons":
      return {
        id: filter.Id,
        title: filter.Title,
        type,
        options: filter.Values.map(filter => ({
          id: filter.Id,
          title: filter.Title,
          containedItemIds: Object.fromEntries(filter.Items.map(item => [item, true]))
        }))
      };
    case "accordions":
      return {
        id: filter.Id,
        title: filter.Title,
        type,
        options: filter.Values.filter(it => !it.Values.length).map(option => ({
          id: option.Id,
          title: option.Title,
          containedItemIds: Object.fromEntries(option.Items.map(item => [item, true]))
        })),
        nested: filter.Values.filter(it => !!it.Values.length).map(nested => parseFilter("accordions", nested))
      };
  }
}

export { getLibraryQuery, useGetLibraryQuery };
//# sourceMappingURL=query.get-library.js.map
